import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import TabNavigation from '../../components/ui/TabNavigation';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import FAQCategory from './components/FAQCategory';
import TroubleshootingGuide from './components/TroubleshootingGuide';
import ContactSupport from './components/ContactSupport';

const FAQHelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('faq'); // 'faq' or 'troubleshooting'

  // FAQ Data organized by category
  const faqCategories = [
    {
      id: 'family-setup',
      title: 'Family Setup',
      icon: 'users',
      color: '#EF4444',
      questions: [
        {
          id: 'add-member',
          question: 'How do I add a new family member?',
          answer: 'Navigate to Settings > Family Members and click "Add Member". Enter their name, role, and assign a color for easy identification across the calendar and tasks.'
        },
        {
          id: 'assign-colors',
          question: 'Can I change family member colors?',
          answer: 'Yes! Go to Settings > Family Members, select the member, and choose a new color from the color picker. This color will update across all views.'
        },
        {
          id: 'remove-member',
          question: 'How do I remove a family member?',
          answer: 'In Settings > Family Members, click on the member you want to remove and select "Remove Member". Note: This will not delete their associated tasks or calendar events.'
        },
        {
          id: 'member-roles',
          question: 'What are member roles and how do they work?',
          answer: 'Member roles (Mom, Dad, Child, etc.) help organize your family structure. They are primarily for display purposes and can be customized to fit your family dynamic.'
        }
      ]
    },
    {
      id: 'calendar-management',
      title: 'Calendar Management',
      icon: 'calendar-days',
      color: '#3B82F6',
      questions: [
        {
          id: 'add-event',
          question: 'How do I add a calendar event?',
          answer: 'Click on any date in the calendar view and select "Add Event". Fill in the event details including title, time, assigned family members, and optional reminders.'
        },
        {
          id: 'recurring-events',
          question: 'Can I create recurring events?',
          answer: 'Yes! When creating or editing an event, enable "Recurring" and choose your pattern (daily, weekly, monthly). You can also set an end date or number of occurrences.'
        },
        {
          id: 'google-calendar-sync',
          question: 'How do I sync with Google Calendar?',
          answer: 'Go to Settings > Google Calendar Settings and click "Connect Google Calendar". Follow the authorization prompts to link your account. Events will sync automatically.'
        },
        {
          id: 'calendar-views',
          question: 'What calendar views are available?',
          answer: 'You can switch between Month, Week, and Day views using the buttons at the top of the calendar. Each view provides different levels of detail for your schedule.'
        },
        {
          id: 'event-reminders',
          question: 'How do I set up event reminders?',
          answer: 'When creating an event, scroll to the "Reminders" section and add one or more reminders. You can set them for specific times before the event (e.g., 15 minutes, 1 hour, 1 day).'
        }
      ]
    },
    {
      id: 'task-coordination',
      title: 'Task Coordination',
      icon: 'check-square',
      color: '#10B981',
      questions: [
        {
          id: 'daily-vs-longterm',
          question: 'What\'s the difference between Daily Tasks and Long Term Tasks?',
          answer: 'Daily Tasks are time-slotted activities for today with specific start times. Long Term Tasks are projects and goals with deadlines that span multiple days or weeks.'
        },
        {
          id: 'assign-tasks',
          question: 'How do I assign tasks to family members?',
          answer: 'When creating a task, use the "Assign To" dropdown to select one or multiple family members. Their assigned color will appear on the task card.'
        },
        {
          id: 'task-priorities',
          question: 'How do task priorities work?',
          answer: 'Tasks can be marked as Low, Medium, or High priority. High priority tasks appear with a red indicator and are sorted to the top of lists.'
        },
        {
          id: 'recurring-tasks',
          question: 'Can I create recurring tasks?',
          answer: 'Yes! Enable "Recurring" when creating a task and set your schedule (daily, weekly, monthly). Perfect for chores, homework, or regular activities.'
        },
        {
          id: 'task-categories',
          question: 'How do I organize tasks by category?',
          answer: 'Tasks can be assigned categories like Household, School, Work, Personal, etc. Use the filter options to view tasks by specific categories.'
        }
      ]
    },
    {
      id: 'messaging',
      title: 'Messaging',
      icon: 'message-square',
      color: '#EC4899',
      questions: [
        {
          id: 'send-message',
          question: 'How do I send a message to family members?',
          answer: 'Go to the Messages tab, select a conversation or start a new one, type your message, and press Send. You can also attach photos or create quick polls.'
        },
        {
          id: 'grocery-list',
          question: 'How does the shared grocery list work?',
          answer: 'In the Messages tab, access the Grocery Checklist. Add items that all family members can see and check off. Items are organized by category for easy shopping.'
        },
        {
          id: 'message-notifications',
          question: 'How do I manage message notifications?',
          answer: 'Go to Settings > Notification Settings and customize your message notification preferences. You can enable/disable sounds, badges, and set quiet hours.'
        },
        {
          id: 'quick-replies',
          question: 'What are Quick Replies?',
          answer: 'Quick Replies are pre-set responses like "On my way", "Done", "Need help" that you can send with one tap. They appear above the message input field.'
        }
      ]
    },
    {
      id: 'settings-configuration',
      title: 'Settings & Configuration',
      icon: 'settings',
      color: '#8B5CF6',
      questions: [
        {
          id: 'parental-lock',
          question: 'How do I set up Parental Lock?',
          answer: 'Go to Settings > Parental Lock and enable the feature. Set a 4-digit PIN and choose which features to protect (e.g., Settings, Profile editing, Task deletion).'
        },
        {
          id: 'dark-mode',
          question: 'How do I enable Dark Mode?',
          answer: 'Go to Profile > Display Preferences and toggle "Dark Mode". The interface will switch to a dark color scheme that\'s easier on the eyes in low light.'
        },
        {
          id: 'backup-restore',
          question: 'How do I backup my family data?',
          answer: 'Navigate to Settings > Backup & Restore and click "Create Backup". Your data will be exported as a JSON file. You can restore it later using "Restore from Backup".'
        },
        {
          id: 'notification-settings',
          question: 'How do I customize notifications?',
          answer: 'Go to Settings > Notification Settings to configure global preferences, or Profile > Personal Notifications for individual settings. You can control sounds, badges, and quiet hours.'
        },
        {
          id: 'achievements',
          question: 'What are Achievements and how do I earn them?',
          answer: 'Achievements are rewards for completing tasks and reaching milestones. View them in Settings > Achievements. Earn badges by completing tasks on time, maintaining streaks, and more.'
        }
      ]
    }
  ];

  // Featured help topics
  const featuredTopics = [
    {
      id: 'getting-started',
      title: 'Getting Started Guide',
      description: 'Complete walkthrough for new users',
      icon: 'book-open',
      color: '#3B82F6'
    },
    {
      id: 'sync-calendar',
      title: 'Sync Google Calendar',
      description: 'Connect your existing calendar',
      icon: 'refresh-cw',
      color: '#10B981'
    },
    {
      id: 'task-tips',
      title: 'Task Management Tips',
      description: 'Best practices for organizing tasks',
      icon: 'lightbulb',
      color: '#F59E0B'
    },
    {
      id: 'family-coordination',
      title: 'Family Coordination',
      description: 'Tips for better family communication',
      icon: 'users',
      color: '#EC4899'
    }
  ];

  // Filter FAQs based on search query
  const filteredCategories = useMemo(() => {
    if (!searchQuery?.trim()) return faqCategories;

    const query = searchQuery?.toLowerCase();
    return faqCategories?.map(category => ({
        ...category,
        questions: category?.questions?.filter(
          q => q?.question?.toLowerCase()?.includes(query) ||
               q?.answer?.toLowerCase()?.includes(query)
        )
      }))?.filter(category => category?.questions?.length > 0);
  }, [searchQuery]);

  // Search result count
  const totalResults = useMemo(() => {
    return filteredCategories?.reduce((sum, cat) => sum + cat?.questions?.length, 0);
  }, [filteredCategories]);

  return (
    <>
      <Helmet>
        <title>Help Center - Starling Schedule</title>
        <meta name="description" content="Get help with FamilySync - browse FAQs, troubleshooting guides, and contact support" />
      </Helmet>

      <TabNavigation />

      <div className="min-h-screen bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
                <Icon name="help-circle" size={24} className="text-primary" />
              </div>
              <div>
                <h1 className="text-h1 font-heading text-foreground">FAQ & Help Center</h1>
                <p className="text-muted-foreground">Find answers to common questions and get support</p>
              </div>
            </div>
          </div>

          {/* Search Bar */}
          <div className="mb-8">
            <div className="relative">
              <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none">
                <Icon name="search" size={20} className="text-muted-foreground" />
              </div>
              <Input
                type="search"
                placeholder="Search help articles, FAQs, and troubleshooting guides..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
                className="pl-12 h-12 text-base"
              />
            </div>
            {searchQuery && (
              <div className="mt-2 text-sm text-muted-foreground">
                Found {totalResults} result{totalResults !== 1 ? 's' : ''} for "{searchQuery}"
              </div>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="mb-6 flex gap-2 border-b border-border">
            <button
              onClick={() => setActiveTab('faq')}
              className={`px-4 py-2 font-medium transition-colors relative ${
                activeTab === 'faq' ?'text-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Frequently Asked Questions
              {activeTab === 'faq' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
              )}
            </button>
            <button
              onClick={() => setActiveTab('troubleshooting')}
              className={`px-4 py-2 font-medium transition-colors relative ${
                activeTab === 'troubleshooting' ?'text-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Troubleshooting Guides
              {activeTab === 'troubleshooting' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />
              )}
            </button>
          </div>

          {/* Featured Topics (only show when no search) */}
          {!searchQuery && activeTab === 'faq' && (
            <div className="mb-8">
              <h2 className="text-h3 font-heading text-foreground mb-4">Featured Help Topics</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {featuredTopics?.map((topic) => (
                  <button
                    key={topic?.id}
                    className="bg-card rounded-lg shadow-warm-sm p-4 hover:shadow-warm-md transition-shadow text-left"
                  >
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                      style={{ backgroundColor: `${topic?.color}20` }}
                    >
                      <Icon name={topic?.icon} size={20} style={{ color: topic?.color }} />
                    </div>
                    <h3 className="font-semibold text-foreground mb-1">{topic?.title}</h3>
                    <p className="text-sm text-muted-foreground">{topic?.description}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Main Content */}
          {activeTab === 'faq' ? (
            <div className="space-y-6">
              {filteredCategories?.length > 0 ? (
                filteredCategories?.map((category) => (
                  <FAQCategory key={category?.id} category={category} searchQuery={searchQuery} />
                ))
              ) : (
                <div className="bg-card rounded-lg shadow-warm-sm p-12 text-center">
                  <Icon name="search-x" size={48} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-h4 font-heading text-foreground mb-2">No results found</h3>
                  <p className="text-muted-foreground mb-4">
                    We couldn't find any FAQs matching "{searchQuery}"
                  </p>
                  <Button onClick={() => setSearchQuery('')} variant="outline">
                    Clear Search
                  </Button>
                </div>
              )}
            </div>
          ) : (
            <TroubleshootingGuide searchQuery={searchQuery} />
          )}

          {/* Contact Support Section */}
          <div className="mt-12">
            <ContactSupport />
          </div>

          {/* Quick Links */}
          <div className="mt-8 bg-card rounded-lg shadow-warm-sm p-6">
            <h3 className="text-h4 font-heading text-foreground mb-4">Quick Links</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button
                variant="outline"
                iconName="book-open"
                onClick={() => window.location.href = '/welcome-onboarding'}
                className="justify-start"
              >
                View Tutorial
              </Button>
              <Button
                variant="outline"
                iconName="settings"
                onClick={() => window.location.href = '/family-settings-management'}
                className="justify-start"
              >
                Go to Settings
              </Button>
              <Button
                variant="outline"
                iconName="user-circle"
                onClick={() => window.location.href = '/individual-profile-management'}
                className="justify-start"
              >
                Manage Profile
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQHelpCenter;